import React, { useCallback } from "react";
import {
  Text,
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
} from "react-native";

import AsyncStorage from "@react-native-async-storage/async-storage";
import { Formik } from "formik";
import { AuthContext } from "../../contexts/auth/Store";
import * as Yup from "yup";

import { AnalyticsService } from "../../constants/Analytics";
import FormikInput from "../../components/form/FormikInput";
import FormikSelectList from "../../components/form/FormikSelectList";
import Colors from "../../constants/Colors";
import StyledButton from "../../components/StyledButton";
import FontStyles from "../../constants/FontStyle";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import http from "../../utils/http";
import GenericHeader from "../../components/headers/GenericHeader";

const analytics = new AnalyticsService();

const ProfileSchema = Yup.object().shape({
  person: Yup.string().required("Enter your name"),
  name: Yup.string().required("Enter your store name"),
  location_id: Yup.string().required("Select your location"),
  store_type_id: Yup.string().required("Select your store type"),
  address: Yup.string().required("Address is required"),
});

export function ProfileScreen() {
  const navigation = useNavigation();
  const auth: any = React.useContext(AuthContext);
  const buyer = auth.getUserData.buyerData;
  const [storeTypes, setStoreTypes] = React.useState([]);
  const [locations, setLocations] = React.useState([]);

  useFocusEffect(
    useCallback(() => {
      (async () => {
        let store_types: any = await AsyncStorage.getItem("storeTypes");
        let store_locations: any = await AsyncStorage.getItem("storeLocations");

        store_types = JSON.parse(store_types);
        store_locations = JSON.parse(store_locations);

        setStoreTypes(store_types || []);
        setLocations(store_locations || []);

        http.post("store_type/read_all", null).then((data) => {
          let list = data.data.filter((item: any) => {
            return item.status === "active" && item.name !== "Default*";
          });
          AsyncStorage.setItem("storeTypes", JSON.stringify(list));

          setStoreTypes(list);
        });

        http.post("location/read_all", null).then((data) => {
          let list = data.data.filter((item: any) => {
            return item.status === "active" && item.name !== "Other*";
          });
          AsyncStorage.setItem("storeLocations", JSON.stringify(list));
          setLocations(list);
        });
      })();
      return () => null;
    }, [])
  );

  return (
    <View style={[styles.container, styles.scrollView]}>
      <GenericHeader title={`Profile`} backAction={() => navigation.goBack()} />
      <ScrollView
        contentContainerStyle={[
          { justifyContent: "flex-end" },
          // styles.scrollView
        ]}
      >
        <KeyboardAvoidingView
          keyboardVerticalOffset={-100}
          enabled={true}
          behavior={"position"}
        >
          {buyer ? (
            <Formik
              initialValues={{
                _id: buyer._id,
                person: buyer.person,
                phone: buyer.phone,
                name: buyer.name,
                store_type_id: buyer.store_type_id,
                location_id: buyer.location_id,
                address: buyer.address,
                status: buyer.status,
                user_id: buyer.user_id,
              }}
              validationSchema={ProfileSchema}
              onSubmit={(values, actions) => {
                actions.setSubmitting(true);
                let form = new FormData();

                form.append("_id", values._id);
                form.append("person", values.person);
                form.append("phone", values.phone);
                form.append("name", values.name);
                form.append("store_type_id", values.store_type_id);
                form.append("location_id", values.location_id);
                form.append("address", values.address);
                form.append("status", values.status);
                form.append("user_id", values.user_id);

                http
                  .post("buyer/update", form, auth.getUserData.userToken)
                  .then((res) => {
                    actions.setSubmitting(false);
                    if (res.code == 200 && res.message) {
                      const location_data = locations.find(
                        (item: any) => item._id === values.location_id
                      );
                      const updatedBuyer = Object.assign({}, buyer, values, {
                        location: location_data,
                      });
                      auth.updateUserData(updatedBuyer);
                      // ToastAndroid.show('Profile update successful', ToastAndroid.LONG, ToastAndroid.BOTTOM)
                      actions.resetForm({ values });
                      analytics.logEvent("Updated Profile", updatedBuyer);
                    } else {
                      // ToastAndroid.show('Profile update failed', ToastAndroid.LONG, ToastAndroid.BOTTOM)
                    }
                  });
              }}
            >
              {({ handleSubmit, isSubmitting, dirty, resetForm }) => (
                <View style={styles.formContainer}>
                  {/* form goes here */}
                  <FormikInput
                    label="Your name"
                    fieldName="person"
                    placeholder="Enter your name"
                  />
                  <FormikInput
                    label="Your phone"
                    fieldName="phone"
                    placeholder="080--------"
                    editable={false}
                  />
                  <FormikInput
                    label="Store Name"
                    fieldName="name"
                    placeholder="Enter your store name"
                  />

                  <FormikSelectList
                    fieldName="store_type_id"
                    label="Store type"
                    placeholder="Select store type"
                    options={storeTypes}
                  />

                  <FormikSelectList
                    fieldName="location_id"
                    label="Store location"
                    placeholder="Select store location"
                    options={locations}
                  />

                  <FormikInput
                    label="Address"
                    placeholder="Address"
                    fieldName="address"
                    multiline={true}
                    numberOfLines={3}
                  />

                  <StyledButton
                    label="Update Profile"
                    action={handleSubmit}
                    disabled={isSubmitting || !dirty}
                    submitting={isSubmitting}
                    style={{
                      height: 40,
                      marginTop: 35,
                    }}
                  />

                  {dirty && (
                    <StyledButton
                      label="Discard Changes"
                      color="light"
                      action={resetForm}
                      disabled={isSubmitting}
                      style={{
                        height: 40,
                        marginTop: 10,
                      }}
                    />
                  )}
                </View>
              )}
            </Formik>
          ) : (
            <View style={{ paddingTop: 42 }}>
              <ActivityIndicator size={48} color="#3B54EC" />
            </View>
          )}

          <TouchableOpacity
            style={{
              padding: 12,
              marginVertical: 10,
              backgroundColor: Colors.whiteColor,
            }}
            onPress={() => navigation.navigate("About")}
          >
            <Text
              style={[
                FontStyles.size13,
                FontStyles.lh15,
                FontStyles.medium,
                {
                  color: Colors.darkColor,
                  textAlign: "center",
                  textTransform: "uppercase",
                },
              ]}
            >
              About
            </Text>
          </TouchableOpacity>
        </KeyboardAvoidingView>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "flex-start",
  },
  scrollView: {
    flexGrow: 1,
    height: "100%",
    backgroundColor: Colors.shades._3,
  },
  formContainer: {
    backgroundColor: Colors.whiteColor,
    padding: 16,
  },
});
